import { EditAction } from "../../../../../@types/enums";

type StartingCorner = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

type Point2D = { x: number; z: number };
type Area = { posArr: Point2D[] };

type PlacementRecord = {
    x: number;
    z: number;
    selSize: unknown;
    dir: string;
    moduleId: string;
    overPathIds: string[];
};

type GenerateModulePlacementsParams = {
    areas: Area[];
    startingCorner: StartingCorner;
    effectivePreDir: string;
    moduleDir: string;
    selSize: { w: number; h: number };
    horizontalRowSpace: number;
    verticalRowSpace: number;
    moduleDx: number;
    moduleDz: number;
    edgeBuffer: number;
    modulePlacementStatusOk: string;
    modulePlacementStatusObstruction: string;
    getLimitPosValue: (arr: Point2D[]) => { minX: number; maxX: number; minZ: number; maxZ: number };
    getRoundNum: (value: number, precision: number) => number;
    isPointInPolygon: (point: Point2D, polygon: Point2D[]) => boolean;
    getVerArr: (cPos: Point2D, selSize: unknown, dir: string) => any;
    canPlaceModule: (verInfo: any, roundX: number, roundZ: number, dir: string) => string;
    onPlacementRejected?: (status: string) => void;
    getOverPathIds: (verInfo: any) => string[];
    getIdStr: () => string;
    /** Rotate the fill lattice in the roof-local XZ plane (ballast yaw). */
    latticeRotationRad?: number;
};

const EPS = 1e-6;

function resolveCorner(startingCorner: StartingCorner, effectivePreDir: string): StartingCorner {
    return startingCorner;
}

function getSweepConfig(
    corner: StartingCorner,
    realMinX: number,
    realMaxX: number,
    realMinZ: number,
    realMaxZ: number
) {
    switch (corner) {
        case 'topLeft':
            return { lastZ: realMaxZ, zDir: -1, xStart: realMinX, xDir: 1 };
        case 'topRight':
            return { lastZ: realMaxZ, zDir: -1, xStart: realMaxX, xDir: -1 };
        case 'bottomLeft':
            return { lastZ: realMinZ, zDir: 1, xStart: realMinX, xDir: 1 };
        case 'bottomRight':
            return { lastZ: realMinZ, zDir: 1, xStart: realMaxX, xDir: -1 };
        default:
            return { lastZ: realMaxZ, zDir: -1, xStart: realMinX, xDir: 1 };
    }
}

function shouldContinueSweep(curr: number, min: number, max: number, dir: number) {
    return dir > 0 ? curr <= max + EPS : curr >= min - EPS;
}

function isPointInAnyArea(point: Point2D, areas: Area[], isPointInPolygon: (point: Point2D, polygon: Point2D[]) => boolean) {
    for (const area of areas) {
        if (isPointInPolygon(point, area.posArr)) return true;
    }
    return false;
}

function rotatePoint2D(point: Point2D, angleRad: number): Point2D {
    const c = Math.cos(angleRad);
    const s = Math.sin(angleRad);
    return {
        x: point.x * c - point.z * s,
        z: point.x * s + point.z * c,
    };
}

function rotateAreas(areas: Area[], angleRad: number): Area[] {
    return areas.map((area) => ({
        posArr: area.posArr.map((p) => rotatePoint2D(p, angleRad)),
    }));
}

export function generateModulePlacements(params: GenerateModulePlacementsParams): PlacementRecord[] {
    const {
        areas,
        startingCorner,
        effectivePreDir,
        moduleDir,
        selSize,
        horizontalRowSpace,
        verticalRowSpace,
        moduleDx,
        moduleDz,
        edgeBuffer,
        modulePlacementStatusOk,
        modulePlacementStatusObstruction,
        getLimitPosValue,
        getRoundNum,
        isPointInPolygon,
        getVerArr,
        canPlaceModule,
        onPlacementRejected,
        getOverPathIds,
        getIdStr,
        latticeRotationRad = 0,
    } = params;

    const placements: PlacementRecord[] = [];
    const yaw = Number(latticeRotationRad) || 0;
    const rotateLattice = Math.abs(Math.sin(yaw)) > EPS;
    const sweepAreas = rotateLattice ? rotateAreas(areas, -yaw) : areas;

    let globalRealMinX = Number.POSITIVE_INFINITY;
    let globalRealMaxX = Number.NEGATIVE_INFINITY;
    let globalRealMinZ = Number.POSITIVE_INFINITY;
    let globalRealMaxZ = Number.NEGATIVE_INFINITY;
    let sharedStrideX = 0;
    let sharedStrideZ = 0;

    for (const area of sweepAreas) {
        const { minX, maxX, minZ, maxZ } = getLimitPosValue(area.posArr);
        // Keep mandatory roof-edge clearance (e.g. 4-inch rule) + half module size.
        const gapDeltaX = edgeBuffer + moduleDx / 2;
        const gapDeltaZ = edgeBuffer + moduleDz / 2 + 0.005;

        const realMinZ = minZ + gapDeltaZ;
        const realMaxZ = maxZ - gapDeltaZ;
        const realMinX = minX + gapDeltaX;
        const realMaxX = maxX - gapDeltaX;

        const strideX = moduleDx + horizontalRowSpace;
        const strideZ = moduleDz + verticalRowSpace;
        sharedStrideX = strideX;
        sharedStrideZ = strideZ;

        if (realMinX > realMaxX || realMinZ > realMaxZ) continue;
        globalRealMinX = Math.min(globalRealMinX, realMinX);
        globalRealMaxX = Math.max(globalRealMaxX, realMaxX);
        globalRealMinZ = Math.min(globalRealMinZ, realMinZ);
        globalRealMaxZ = Math.max(globalRealMaxZ, realMaxZ);
    }

    if (!Number.isFinite(globalRealMinX) || !Number.isFinite(globalRealMinZ)) {
        return placements;
    }

    const actualCorner = resolveCorner(startingCorner, effectivePreDir);
    const { zDir, xDir, xStart, lastZ } = getSweepConfig(actualCorner, globalRealMinX, globalRealMaxX, globalRealMinZ, globalRealMaxZ);

    // Strict corner-anchored lattice: start exactly from selected corner edge.
    const cornerPlacements: PlacementRecord[] = [];
    let currZ = lastZ;
    while (shouldContinueSweep(currZ, globalRealMinZ, globalRealMaxZ, zDir)) {
        let currX = xStart;
        while (shouldContinueSweep(currX, globalRealMinX, globalRealMaxX, xDir)) {
            const latticePoint = { x: getRoundNum(currX, 4), z: getRoundNum(currZ, 4) };

            if (!isPointInAnyArea(latticePoint, sweepAreas, isPointInPolygon)) {
                currX += sharedStrideX * xDir;
                continue;
            }

            const worldPoint = rotateLattice
                ? rotatePoint2D(latticePoint, yaw)
                : latticePoint;
            const roundX = getRoundNum(worldPoint.x, 4);
            const roundZ = getRoundNum(worldPoint.z, 4);
            const point = { x: roundX, z: roundZ };

            const verInfo = getVerArr(point, selSize, moduleDir);
            const result = canPlaceModule(verInfo, roundX, roundZ, moduleDir);

            if (result === modulePlacementStatusOk) {
                cornerPlacements.push({
                    x: roundX,
                    z: roundZ,
                    selSize,
                    dir: moduleDir,
                    moduleId: getIdStr(),
                    overPathIds: getOverPathIds(verInfo),
                });
                currX += sharedStrideX * xDir;
                continue;
            }

            if (onPlacementRejected) onPlacementRejected(result);

            // Keep strict row/column alignment.
            // If a slot is blocked (e.g. obstruction), skip to the next lattice slot
            // instead of fine-scanning in-between positions.
            currX += sharedStrideX * xDir;
        }
        currZ += sharedStrideZ * zDir;
    }

    placements.push(...cornerPlacements);
    return placements;
}

const MODULE_FILL_SIM_EVENT = 'module-fill-simulation';
const DEFAULT_SIMULATION_DELAY_MS = 140;

type FillingContext = any;

function emitModuleFillSimulationEvent(payload: any) {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(MODULE_FILL_SIM_EVENT, { detail: payload }));
}

function clearModuleFillSimulationTimer(self: any) {
    if (!self?.moduleFillSimulationTimer) return;
    clearTimeout(self.moduleFillSimulationTimer);
    self.moduleFillSimulationTimer = null;
}

function finalizeRoofAutofill(context: FillingContext, self: any, roofMesh: any, face: any, selDir: any) {
    context.siteCalculationState.needsUpdate(context.MODULE_GRAPH);
    context.UpdateSetbackDepth(self, 'autoFill', selDir);
    const { lines } = face || {};
    lines?.forEach((line: any) => context.SetPathwayActiveModule(self, line.lineId));

    roofMesh.children
        .filter((item: any) => item.meshType === 'module')
        .forEach((module: any) => {
            module.wPos = module.getWorldPosition(new context.THREE.Vector3());
        });
}

function simulateRoofAutofill(
    context: FillingContext,
    self: any,
    roofMesh: any,
    moduleArr: any[],
    face: any,
    selDir: any,
    showLabel: boolean,
    simulationDelayMs: number
) {
    clearModuleFillSimulationTimer(self);

    const total = moduleArr.length;
    const roofNumber = roofMesh?.userData?.roof_tag_id ?? roofMesh?.userData?.roof_number_from_aurora ?? roofMesh?.roofFaceId;
    const obstructionInfo = roofMesh?.userData?.fillDebugObstructionInfo || {};
    const obstructionIds = Array.isArray(obstructionInfo.obstructionIds) ? obstructionInfo.obstructionIds : [];
    const obstructionHitCount = Number(obstructionInfo.obstructionHitCount || 0);

    emitModuleFillSimulationEvent({
        phase: 'start',
        roofFaceId: roofMesh?.roofFaceId,
        roofNumber,
        total,
        obstructionIds,
        obstructionHitCount,
        delayMs: simulationDelayMs,
    });

    if (!total) {
        finalizeRoofAutofill(context, self, roofMesh, face, selDir);
        emitModuleFillSimulationEvent({
            phase: 'complete',
            roofFaceId: roofMesh?.roofFaceId,
            roofNumber,
            total,
            placed: 0,
            obstructionIds,
            obstructionHitCount,
        });
        return;
    }

    let index = 0;
    const placeNext = () => {
        if (index >= total) {
            self.moduleFillSimulationTimer = null;
            finalizeRoofAutofill(context, self, roofMesh, face, selDir);
            emitModuleFillSimulationEvent({
                phase: 'complete',
                roofFaceId: roofMesh?.roofFaceId,
                roofNumber,
                total,
                placed: total,
                obstructionIds,
                obstructionHitCount,
            });
            return;
        }

        const item = moduleArr[index];
        context.AddIndividualMode(roofMesh, item, true, self.moduleMeshArr, self.meshArr, showLabel);
        emitModuleFillSimulationEvent({
            phase: 'step',
            roofFaceId: roofMesh?.roofFaceId,
            roofNumber,
            total,
            index: index + 1,
            x: item?.x,
            z: item?.z,
            dir: item?.dir,
            moduleId: item?.moduleId,
            obstructionIds,
            obstructionHitCount,
        });

        index += 1;
        self.moduleFillSimulationTimer = setTimeout(placeNext, simulationDelayMs);
    };

    placeNext();
}

export function computeBestArrangementForRoof(context: FillingContext, params: any) {
    const {
        roofMesh, pos2DArr, holeArr, rotX, setbackArr, pathwayArr, selSize, selDir, obstMeshArr, wallMeshArr, maxCount = Infinity,
    } = params;
    const STARTING_CORNERS = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'];
    const orientationsToTryAll = Object.values(context.ModuleOrientation);

    const bestForOrientation: any = {
        port: { arr: [], count: 0, corner: STARTING_CORNERS[0] },
        both: { arr: [], count: 0, corner: STARTING_CORNERS[0] },
        land: { arr: [], count: 0, corner: STARTING_CORNERS[0] },
    };

    const evaluateOrientation = (orientation: any, forcedCorner: any = null) => {
        let bestCount = 0;
        let bestArray: any[] = [];
        let bestCorner = forcedCorner || STARTING_CORNERS[0];
        const cornersToTry = forcedCorner ? [forcedCorner] : STARTING_CORNERS;

        for (const startingCorner of cornersToTry) {
            const moduleArr = context.GetModuleFaceArr(
                roofMesh, pos2DArr, holeArr, obstMeshArr, selSize, orientation, rotX, setbackArr, pathwayArr, true, wallMeshArr, startingCorner, maxCount
            );
            if (moduleArr.length > bestCount) {
                bestCount = moduleArr.length;
                bestArray = moduleArr;
                bestCorner = startingCorner;
            }
        }
        return { arr: bestArray, count: bestCount, corner: bestCorner };
    };

    const orientationsToTry = selDir ? [selDir] : orientationsToTryAll;
    for (const orientation of orientationsToTry) {
        // Evaluate all corners so lattice anchoring can adapt to edge-constrained layouts.
        bestForOrientation[orientation] = evaluateOrientation(orientation);
    }

    if (selDir) return bestForOrientation[selDir].arr;
    const { port, both, land } = bestForOrientation;
    if (port.count > both.count) return port.arr;
    if (both.count > port.count) return both.arr;
    if (port.count === 0 && both.count === 0) return land.arr;
    return port.arr;
}

/**
 * Build a setback array using the requested width, regardless of the current
 * `userData.use18` flag (which may be stale during fill flows). Used so the
 * 33% rule's setback decision drives the actual computation deterministically.
 */
function buildSetbackArrForced(roofGroup: any, roofFaceId: any, force36: boolean): any[] {
    const setbackArr: any[] = [];
    roofGroup.traverse((child: any) => {
        if (child?.pathWayRoofId !== roofFaceId) return;
        if (child?.meshType !== 'setback') return;
        if (child?.isMarkedInactive) return;
        const has18 = Array.isArray(child.pathwayPosArr18) && child.pathwayPosArr18.length > 0;
        const posArr = (!force36 && has18) ? child.pathwayPosArr18 : child.pathwayPosArr;
        if (posArr) setbackArr.push({ type: 'setback', posArr });
    });
    return setbackArr;
}

/**
 * Compute per-roof unconstrained module counts (assuming 18" setbacks) and
 * cache them on each roof as `_predictedFillCount`. Used by the 33% rule
 * sibling check so default maxfill and autofill see an identical baseline.
 *
 * If `buildingNameFilter` is provided, only roofs in that building are
 * recomputed (used by per-roof autofill to avoid scanning the whole project).
 */
export function precomputePredictedFillCounts(
    context: FillingContext,
    self: any,
    selSize: any,
    selDir: any,
    buildingNameFilter?: any
) {
    const { roofMeshArr, roofGroup, obstGroup, wallMeshArr, accessoryBuildingList } = self;
    const obstMeshArr = obstGroup.children.map((c: any) => c.children[0].children[0].children[0]);

    for (const roofMesh of roofMeshArr) {
        if (buildingNameFilter !== undefined && roofMesh.userData?.buildingName !== buildingNameFilter) {
            continue;
        }
        if (!context.isEligibleRoofForModules(roofMesh, accessoryBuildingList)
            || (roofMesh.size && roofMesh.size < 36.11111)
            || context.isRoofTooSteep(roofMesh.oriAng?.tilt)) {
            roofMesh._predictedFillCount = 0;
            continue;
        }

        const { rotX, pos2DArr, holeArr, roofFaceId } = roofMesh;
        const { pathwayArr } = context.GetSetPathArr(roofGroup, roofFaceId);
        const setbackArr = buildSetbackArrForced(roofGroup, roofFaceId, false);

        const moduleArr = computeBestArrangementForRoof(context, {
            roofMesh, pos2DArr, holeArr, rotX, setbackArr, pathwayArr, selSize, selDir, obstMeshArr, wallMeshArr,
        });
        roofMesh._predictedFillCount = moduleArr.length;
    }
}

function getRoofPredictedCount(context: FillingContext, roofMesh: any): number {
    if (typeof roofMesh._predictedFillCount === 'number') return roofMesh._predictedFillCount;
    return context.getRoofModuleMeshes(roofMesh).length;
}

function findBuildingFor33Rule(context: FillingContext, self: any, buildingName: any) {
    const match = (b: any) => b?.name === buildingName || String(b?.name) === String(buildingName);
    const fromViewer = self.buildingArr?.find(match);
    if (fromViewer) return fromViewer;
    const fromStore = context.useViewerStore.getState().buildingWise33PercentRuleModuleCountList?.find(match);
    return fromStore;
}

export function computeArrangementWithAutofillGuards(context: FillingContext, params: any) {
    const { self, roofMesh, selSize, selDir, obstMeshArr, wallMeshArr, renderDebugAllowedAreas = false, debugVariant = 'autofill', maxCount = Infinity } = params;
    const { roofGroup } = self;
    const { rotX, pos2DArr, holeArr, roofFaceId } = roofMesh;
    const shouldRenderAllowedAreas = context.isOneDrawDebugMode() && renderDebugAllowedAreas;

    // Decide setback width up-front using a deterministic, order-independent
    // baseline so default maxfill and per-roof autofill agree on whether the
    // building exceeds the 33% threshold.
    const buildingName = roofMesh.userData?.buildingName;
    const currentBuilding = findBuildingFor33Rule(context, self, buildingName);
    const isSetbackOverride = !!context.useViewerStore.getState().isSetbackOverride;
    const modeCount33 = Number(currentBuilding?.modeCount33) || 0;

    let force36Setback = isSetbackOverride || !!currentBuilding?.mode33Over;
    if (!force36Setback && modeCount33 > 0) {
        const sameBuilding = (r: any) =>
            r.userData?.buildingName === buildingName || String(r.userData?.buildingName) === String(buildingName);
        const siblingRoofs = self.roofMeshArr.filter(
            (r: any) => sameBuilding(r) && r.roofFaceId !== roofFaceId
        );
        const siblingPredicted = siblingRoofs.reduce(
            (acc: number, r: any) => acc + getRoofPredictedCount(context, r),
            0
        );
        const thisPredicted = getRoofPredictedCount(context, roofMesh);
        // Also treat currently-placed modules as over-threshold (mode33Over can be stale).
        const placedOnBuilding = self.roofMeshArr
            .filter(sameBuilding)
            .reduce((acc: number, r: any) => acc + context.getRoofModuleMeshes(r).length, 0);
        if (
            siblingPredicted + thisPredicted >= modeCount33
            || placedOnBuilding >= modeCount33
        ) {
            force36Setback = true;
        }
    }

    const { pathwayArr } = context.GetSetPathArr(roofGroup, roofFaceId, force36Setback);
    const setbackArr = buildSetbackArrForced(roofGroup, roofFaceId, force36Setback);
    if (shouldRenderAllowedAreas) {
        const allowedAreasForDebug = context.GetAllowedAreas(pos2DArr, setbackArr, pathwayArr);
        context.renderAllowedAreasOverlay(roofMesh, allowedAreasForDebug, debugVariant);
    }

    return computeBestArrangementForRoof(context, {
        roofMesh, pos2DArr, holeArr, rotX, setbackArr, pathwayArr, selSize, selDir, obstMeshArr, wallMeshArr, maxCount,
    });
}

export function addModulesToAllRoofsMaxFill(
    context: FillingContext,
    self: any,
    selSize: any = null,
    selDir: any = null,
    selectedRoofFaceIds: any = null
) {
    const { roofMeshArr, roofs, obstGroup, wallMeshArr, accessoryBuildingList } = self;
    context.SetObstVPos(obstGroup);
    context.initForModulePlacement(self);
    if (self.viewMode === '2d') self.setCameraView('3d');

    if (!selSize) selSize = context.useViewerStore.getState().activeModule;
    const showLabel = context.useViewerStore.getState().isModuleMultiplierLabelVisible;
    self._isDefaultFillDebugActive = true;
    const originalCamZoom = self.camera.zoom;
    const originalCamPos = self.camera.position.clone();
    const originalTarget = self.controls.target.clone();

    const oldModeArrInfo = context.useViewerStore.getState().savedRoofModulesLayout || [];
    const modeArrInfo = context.cloneDeep(oldModeArrInfo);

    // Precompute per-roof unconstrained predicted counts so the 33% rule sees
    // the same building total regardless of processing order, and matches what
    // a per-roof autofill would compute later.
    precomputePredictedFillCounts(context, self, selSize, selDir);

    for (const roofMesh of roofMeshArr) {
        context.clearDefaultFillCornerDebug(roofMesh);
        if (
            selectedRoofFaceIds != null &&
            Array.isArray(selectedRoofFaceIds) &&
            selectedRoofFaceIds.length > 0 &&
            !selectedRoofFaceIds.includes(roofMesh.roofFaceId)
        ) {
            continue;
        }
        if (!context.isEligibleRoofForModules(roofMesh, accessoryBuildingList)) continue;
        if (roofMesh.size && roofMesh.size < 36.11111) continue;

        const roofFaceId = roofMesh.roofFaceId;
        const { tilt } = roofMesh.oriAng || {};
        if (context.isRoofTooSteep(tilt)) {
            context.ensureModeInfoEntry(modeArrInfo, roofFaceId);
            context.useViewerStore.setState({ savedRoofModulesLayout: modeArrInfo });
            continue;
        }

        context.focusCameraToRoofForFill(self, roofMesh);
        const obstMeshArr = obstGroup.children.map((c: any) => c.children[0].children[0].children[0]);
        const moduleArr = computeArrangementWithAutofillGuards(context, {
            self, roofMesh, selSize, selDir, obstMeshArr, wallMeshArr, renderDebugAllowedAreas: true, debugVariant: 'default',
        });

        const face = roofs[0].faces.find((f: any) => f.roofFaceId === roofFaceId);
        if (face) face.moduleArr = moduleArr;
        roofMesh.moduleArr = moduleArr;
        context.updateModeArrInfoForRoof(modeArrInfo, roofFaceId, moduleArr);
        context.addModulesToScene(roofMesh, moduleArr, self, showLabel);
    }

    self.camera.zoom = originalCamZoom;
    self.camera.position.copy(originalCamPos);
    self.controls.target.copy(originalTarget);
    self.camera.updateProjectionMatrix();
    self.controls.update();
    self._isDefaultFillDebugActive = false;

    context.applyPostProcessing(self, modeArrInfo, null);
    for (const roofMesh of roofMeshArr) {
        roofMesh.children
            .filter((item: any) => item.meshType === 'module')
            .forEach((module: any) => {
                module.wPos = module.getWorldPosition(new context.THREE.Vector3());
            });
    }
}

export function addModulesToRoofAutoFill(
    context: FillingContext,
    self: any,
    roofMesh: any,
    selSize: any = null,
    selDir: any = null,
    options: any = {}
) {
    const { simulate = false, simulationDelayMs = DEFAULT_SIMULATION_DELAY_MS, removeExisting = true, maxCount = Infinity, onlyBallasted = false } = options || {};
    if (!roofMesh) return [];

    const { roofs, obstGroup, wallMeshArr, moduleMeshArr, accessoryBuildingList } = self;
    if (!context.isEligibleRoofForModules(roofMesh, accessoryBuildingList)) return [];
    if (roofMesh.size && roofMesh.size < 36.11111) return [];
    if (context.isRoofTooSteep(roofMesh.oriAng?.tilt)) return [];

    context.SetObstVPos(obstGroup);
    if (!selSize) selSize = context.useViewerStore.getState().activeModule;
    const showLabel = context.useViewerStore.getState().isModuleMultiplierLabelVisible;

    if (removeExisting) {
        const modules = roofMesh.children.filter((child: any) => child?.meshType === 'module');
        context.DeleteModule(modules, roofMesh, moduleMeshArr, self, 'autoFill');
    }

    // Use the same building-wide predicted-count baseline as default maxfill so
    // both flows take the same setback decision for the same roof. Scope to the
    // target building to keep per-roof autofill fast.
    precomputePredictedFillCounts(context, self, selSize, selDir, roofMesh.userData?.buildingName);

    const { roofFaceId } = roofMesh;
    const obstMeshArr = obstGroup.children.map((c: any) => c.children[0].children[0].children[0]);
    const moduleArr = computeArrangementWithAutofillGuards(context, {
        self, roofMesh, selSize, selDir, obstMeshArr, wallMeshArr, renderDebugAllowedAreas: true, debugVariant: 'autofill', maxCount,
    });

    const face = roofs[0].faces.find((f: any) => f.roofFaceId === roofFaceId);
    if (face) face.moduleArr = moduleArr;
    roofMesh.moduleArr = moduleArr;

    const oldModeArrInfo = context.useViewerStore.getState().savedRoofModulesLayout || [];
    const modeArrInfo = context.cloneDeep(oldModeArrInfo);
    context.updateModeArrInfoForRoof(modeArrInfo, roofFaceId, moduleArr);
    context.useViewerStore.setState({ savedRoofModulesLayout: modeArrInfo });

    if (typeof self?.addStep === 'function') {
        self.addStep(
            EditAction.ModuleLayout,
            roofFaceId,
            JSON.stringify(oldModeArrInfo),
            JSON.stringify(modeArrInfo)
        );
    }

    if (simulate) {
        simulateRoofAutofill(context, self, roofMesh, moduleArr, face, selDir, showLabel, simulationDelayMs);
    } else {
        context.addModulesToScene(roofMesh, moduleArr, self, showLabel);
        finalizeRoofAutofill(context, self, roofMesh, face, selDir);
    }

    return moduleArr;
}

const POLYGON_CLIP_EPS = 1e-4;

function pointsNearlyEqualForFilling(a: Point2D, b: Point2D, eps = POLYGON_CLIP_EPS) {
    return Math.abs(a.x - b.x) <= eps && Math.abs(a.z - b.z) <= eps;
}

function signedAreaForFilling(points: Point2D[]) {
    let area = 0;
    for (let i = 0; i < points.length; i++) {
        const j = (i + 1) % points.length;
        area += points[i].x * points[j].z - points[j].x * points[i].z;
    }
    return area / 2;
}

function removeCollinearPointsForFilling(points: Point2D[], eps = POLYGON_CLIP_EPS) {
    if (points.length <= 3) return points;

    const result: Point2D[] = [];
    for (let i = 0; i < points.length; i++) {
        const prev = points[(i - 1 + points.length) % points.length];
        const curr = points[i];
        const next = points[(i + 1) % points.length];
        const cross = (curr.x - prev.x) * (next.z - prev.z) - (curr.z - prev.z) * (next.x - prev.x);
        if (Math.abs(cross) > eps) {
            result.push(curr);
        }
    }
    return result;
}

function getPolygonBBoxForFilling(points: Point2D[]) {
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;

    points.forEach((point) => {
        minX = Math.min(minX, point.x);
        maxX = Math.max(maxX, point.x);
        minZ = Math.min(minZ, point.z);
        maxZ = Math.max(maxZ, point.z);
    });

    return { minX, maxX, minZ, maxZ };
}

function bboxesOverlapForFilling(a: ReturnType<typeof getPolygonBBoxForFilling>, b: ReturnType<typeof getPolygonBBoxForFilling>) {
    return !(a.maxX < b.minX || a.minX > b.maxX || a.maxZ < b.minZ || a.minZ > b.maxZ);
}

function ensureCounterClockwiseForFilling(points: Point2D[]) {
    return signedAreaForFilling(points) < 0 ? [...points].reverse() : points;
}

function toPolygonClippingRing(points: Point2D[]) {
    return points.map((point) => [point.x, point.z] as [number, number]);
}

export function cleanPolygonForFilling(polygon: any[]) {
    if (!polygon?.length) return;

    let cleanedPolygon = polygon.filter((point, index, self) => {
        if (index === 0) return true;
        return !pointsNearlyEqualForFilling(point, self[index - 1]);
    });

    if (cleanedPolygon.length > 1 && pointsNearlyEqualForFilling(cleanedPolygon[0], cleanedPolygon[cleanedPolygon.length - 1])) {
        cleanedPolygon = cleanedPolygon.slice(0, -1);
    }

    cleanedPolygon = removeCollinearPointsForFilling(cleanedPolygon);

    if (cleanedPolygon.length < 3) return;

    if (Math.abs(signedAreaForFilling(cleanedPolygon)) < POLYGON_CLIP_EPS * POLYGON_CLIP_EPS) return;

    cleanedPolygon = ensureCounterClockwiseForFilling(cleanedPolygon).map((point) => ({
        x: parseFloat(point.x.toFixed(6)),
        z: parseFloat(point.z.toFixed(6)),
    }));

    return cleanedPolygon;
}

export function subtractPolygonAreasForFilling(context: any, areas: any[], polygonToSubtract: any[]) {
    const newAreas: any[] = [];
    const subtractBBox = getPolygonBBoxForFilling(polygonToSubtract);

    areas.forEach((area) => {
        if (!area?.posArr?.length) return;

        if (!bboxesOverlapForFilling(getPolygonBBoxForFilling(area.posArr), subtractBBox)) {
            newAreas.push(area);
            return;
        }

        try {
            const difference = context.PolygonClipping.difference(
                [toPolygonClippingRing(area.posArr)],
                [toPolygonClippingRing(polygonToSubtract)]
            );
            difference.forEach((diffArea: any[]) => {
                const exteriorRing = diffArea?.[0];
                if (!exteriorRing?.length) return;

                const posArr = exteriorRing.map((p: any) => ({ x: p[0], z: p[1] }));
                const cleaned = cleanPolygonForFilling(posArr);
                if (cleaned) {
                    newAreas.push({ posArr: cleaned });
                }
            });
        } catch (error) {
            console.warn('PolygonClipping difference failed; keeping original allowed area for this subtraction.', error);
            newAreas.push(area);
        }
    });
    return newAreas;
}

export function getAllowedAreasForFilling(context: any, pos2DArr: any[], setbackArr: any[], pathwayArr: any[]) {
    const cleanedPos2DArr = cleanPolygonForFilling(pos2DArr);
    if (!cleanedPos2DArr) return [];

    let allowedAreas = [{ posArr: cleanedPos2DArr }];

    setbackArr.forEach((setback) => {
        if (!setback?.posArr) return;
        const cleanedSetback = cleanPolygonForFilling(setback.posArr);
        if (!cleanedSetback) return;
        allowedAreas = subtractPolygonAreasForFilling(context, allowedAreas, cleanedSetback);
    });

    pathwayArr.forEach((pathway) => {
        if (pathway.pathActive) {
            const cleanedPathway = cleanPolygonForFilling(pathway.posArr);
            if (!cleanedPathway) return;
            allowedAreas = subtractPolygonAreasForFilling(context, allowedAreas, cleanedPathway);
        }
    });

    return allowedAreas;
}

export function isPointOnPolygonBoundaryForFilling(point: any, polygon: any[], eps = 1e-4) {
    if (!polygon || polygon.length < 2) return false;
    for (let i = 0; i < polygon.length; i++) {
        const a = polygon[i];
        const b = polygon[(i + 1) % polygon.length];
        const abx = b.x - a.x;
        const abz = b.z - a.z;
        const apx = point.x - a.x;
        const apz = point.z - a.z;
        const cross = Math.abs(abx * apz - abz * apx);
        if (cross > eps) continue;
        const dot = apx * abx + apz * abz;
        if (dot < -eps) continue;
        const lenSq = abx * abx + abz * abz;
        if (dot - lenSq > eps) continue;
        return true;
    }
    return false;
}

export function isPointInPolygonForFilling(context: any, point: any, polygon: any[]) {
    return isPointOnPolygonBoundaryForFilling(point, polygon) || context.pointInPolygon([point.x, point.z], polygon.map((p) => [p.x, p.z]));
}

export function applyDebugOverlayVisibilityForFilling(context: any, roofMesh: any) {
    if (!roofMesh) return;
    const fillOptions = context.getOneDrawDebuggerOptions().fillDebugger;

    roofMesh.children?.forEach((child: any) => {
        if (child?.name === context.DEFAULT_FILL_CORNER_DEBUG_GROUP) {
            child.visible = !!fillOptions.showStartCorner;
            return;
        }
        const debugOverlayGroup = child?.userData?.debugOverlayGroup;
        if (debugOverlayGroup === context.ALLOWED_AREAS_DEBUG_GROUP) {
            child.visible = !!fillOptions.showAutofillAllowedAreas;
            return;
        }
        if (debugOverlayGroup === context.DEFAULT_ALLOWED_AREAS_DEBUG_GROUP) {
            child.visible = !!fillOptions.showDefaultAllowedAreas;
        }
    });
}

export function clearAllowedAreasOverlayForFilling(context: any, roofMesh: any) {
    if (!roofMesh?.children?.length) return;
    for (let i = roofMesh.children.length - 1; i >= 0; i--) {
        const child = roofMesh.children[i];
        if (
            child?.userData?.debugOverlayGroup === context.ALLOWED_AREAS_DEBUG_GROUP ||
            child?.userData?.debugOverlayGroup === context.DEFAULT_ALLOWED_AREAS_DEBUG_GROUP
        ) {
            roofMesh.remove(child);
        }
    }
}

export function renderAllowedAreasOverlayForFilling(context: any, roofMesh: any, allowedAreas: any[], variant = 'autofill') {
    if (!roofMesh) return;
    const groupName = variant === 'default' ? context.DEFAULT_ALLOWED_AREAS_DEBUG_GROUP : context.ALLOWED_AREAS_DEBUG_GROUP;
    for (let i = roofMesh.children.length - 1; i >= 0; i--) {
        const child = roofMesh.children[i];
        if (child?.userData?.debugOverlayGroup === groupName) {
            roofMesh.remove(child);
        }
    }
    if (!allowedAreas?.length) return;

    const overlayGroup = new context.THREE.Group();
    overlayGroup.userData.debugOverlayGroup = groupName;
    const fillColor = variant === 'default' ? 0xffa500 : 0x00ffea;
    const lineColor = variant === 'default' ? 0xff3b30 : 0x00ff00;
    const vertexColor = variant === 'default' ? 0xffff00 : 0xff00ff;

    allowedAreas.forEach((area: any) => {
        if (!area?.posArr || area.posArr.length < 3) return;
        const shape = new context.THREE.Shape();
        area.posArr.forEach((p: any, idx: number) => {
            if (idx === 0) shape.moveTo(p.x, p.z);
            else shape.lineTo(p.x, p.z);
        });
        shape.closePath();

        const fillGeometry = new context.THREE.ShapeGeometry(shape);
        const fillMaterial = new context.THREE.MeshBasicMaterial({
            color: fillColor, transparent: true, opacity: 0.22, side: context.THREE.DoubleSide, depthWrite: false, depthTest: false,
        });
        const fillMesh = new context.THREE.Mesh(fillGeometry, fillMaterial);
        fillMesh.position.set(0, 0, 0.08);
        fillMesh.renderOrder = 10000;
        overlayGroup.add(fillMesh);

        const linePoints = area.posArr.map((p: any) => new context.THREE.Vector3(p.x, p.z, 0.05));
        const lineGeometry = new context.THREE.BufferGeometry().setFromPoints(linePoints);
        const lineMaterial = new context.THREE.LineBasicMaterial({
            color: lineColor, transparent: true, opacity: 1, depthWrite: false, depthTest: false,
        });
        const outline = new context.THREE.LineLoop(lineGeometry, lineMaterial);
        outline.renderOrder = 10001;
        overlayGroup.add(outline);

        area.posArr.forEach((p: any) => {
            const vertexMarker = new context.THREE.Mesh(
                new context.THREE.SphereGeometry(0.08, 10, 10),
                new context.THREE.MeshBasicMaterial({ color: vertexColor, transparent: true, opacity: 0.95, depthTest: false, depthWrite: false })
            );
            vertexMarker.position.set(p.x, p.z, 0.1);
            vertexMarker.renderOrder = 10002;
            overlayGroup.add(vertexMarker);
        });
    });

    roofMesh.add(overlayGroup);
    applyDebugOverlayVisibilityForFilling(context, roofMesh);
}

export function getViewAlignedStartingCornerForFilling(context: any, roofMesh: any, camera: any, pos2DArr: any[], requestedCorner: string) {
    if (!roofMesh || !pos2DArr?.length) return requestedCorner;
    const { minX, maxX, minZ, maxZ } = context.GetLimitPosValue(pos2DArr);
    const localCornerPoints: any = {
        topLeft: { x: minX, z: maxZ },
        topRight: { x: maxX, z: maxZ },
        bottomLeft: { x: minX, z: minZ },
        bottomRight: { x: maxX, z: minZ },
    };

    roofMesh.updateWorldMatrix(true, false);
    const worldCorners = Object.entries(localCornerPoints).map(([key, p]: any) => {
        const world = new context.THREE.Vector3(p.x, p.z, 0).applyMatrix4(roofMesh.matrixWorld);
        return { key, world };
    });

    let projectionCamera = camera;
    if (!projectionCamera) {
        const center = new context.THREE.Vector3();
        worldCorners.forEach(({ world }: any) => center.add(world));
        center.multiplyScalar(1 / worldCorners.length);
        const maxRadius = Math.max(...worldCorners.map(({ world }: any) => world.distanceTo(center)), 1);
        const viewDir = new context.THREE.Vector3(0, -90, -72).normalize();
        const cameraDistance = Math.max(maxRadius * 3, 120);
        const cameraPos = center.clone().sub(viewDir.clone().multiplyScalar(cameraDistance));
        const virtualCamera = new context.THREE.PerspectiveCamera(45, 1, 0.1, cameraDistance * 10);
        virtualCamera.position.copy(cameraPos);
        virtualCamera.up.set(0, 1, 0);
        virtualCamera.lookAt(center);
        virtualCamera.updateMatrixWorld(true);
        virtualCamera.updateProjectionMatrix();
        projectionCamera = virtualCamera;
    }

    projectionCamera.updateProjectionMatrix();
    projectionCamera.updateMatrixWorld(true);
    const projectedCorners = worldCorners.map(({ key, world }: any) => {
        const ndc = world.clone().project(projectionCamera);
        return { key, ndcX: ndc.x, ndcY: ndc.y };
    });

    const leftX = Math.min(...projectedCorners.map((c: any) => c.ndcX));
    const rightX = Math.max(...projectedCorners.map((c: any) => c.ndcX));
    const topY = Math.max(...projectedCorners.map((c: any) => c.ndcY));
    const bottomY = Math.min(...projectedCorners.map((c: any) => c.ndcY));
    const targetX = requestedCorner.includes('Left') ? leftX : rightX;
    const targetY = requestedCorner.includes('top') ? topY : bottomY;

    let best = projectedCorners[0];
    let bestScore = Number.POSITIVE_INFINITY;
    projectedCorners.forEach((c: any) => {
        const score = Math.abs(c.ndcX - targetX) + Math.abs(c.ndcY - targetY);
        if (score < bestScore) {
            best = c;
            bestScore = score;
        }
    });

    const resolvedCorner = best.key;
    const sceneViewer = context.useViewerStore.getState().sceneViewer;
    if (context.isOneDrawDebugMode() && sceneViewer?._isDefaultFillDebugActive) {
        context.renderDefaultFillStartCornerDebug(roofMesh, pos2DArr, resolvedCorner);
        applyDebugOverlayVisibilityForFilling(context, roofMesh);
    }
    return resolvedCorner;
}
